/**
 * Duplicate Schedule Dialog
 *
 * Reusable dialog component for duplicating schedules.
 * Prompts user for new schedule name and handles duplication.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useDuplicateScheduleMutation } from '../../../api/schedulingApi';

interface DuplicateScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  scheduleId: string;
  scheduleName: string;
  studioId: string;
  onSuccess?: (newScheduleId: string) => void;
}

const DuplicateScheduleDialog: React.FC<DuplicateScheduleDialogProps> = ({
  open,
  onClose,
  scheduleId,
  scheduleName,
  studioId,
  onSuccess,
}) => {
  const [newName, setNewName] = useState(`Copy of ${scheduleName}`);
  const [duplicateSchedule, { isLoading, error }] =
    useDuplicateScheduleMutation();

  const handleDuplicate = async () => {
    if (!newName.trim()) {
      return;
    }

    try {
      const result = await duplicateSchedule({
        id: scheduleId,
        studioId,
        data: { name: newName.trim() },
      }).unwrap();

      // Success - close dialog and optionally navigate to new schedule
      if (onSuccess) {
        onSuccess(result.id);
      }
      onClose();
    } catch (err) {
      console.error('Failed to duplicate schedule:', err);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      // Reset name after a short delay to avoid flickering
      setTimeout(() => {
        setNewName(`Copy of ${scheduleName}`);
      }, 200);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Duplicate Schedule</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="New Schedule Name"
          type="text"
          fullWidth
          variant="outlined"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          disabled={isLoading}
          helperText="Enter a name for the duplicated schedule"
          onKeyPress={e => {
            if (e.key === 'Enter' && newName.trim()) {
              handleDuplicate();
            }
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to duplicate schedule. Please try again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleDuplicate}
          variant="contained"
          disabled={isLoading || !newName.trim()}
          startIcon={isLoading && <CircularProgress size={20} />}
        >
          {isLoading ? 'Duplicating...' : 'Duplicate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateScheduleDialog;
